import React from 'react'
import {UIGameActionButton} from '../../../common/UIGameActionButton'
import IconPencil from '@static/images/icon_pencil.svg'
import {TradeActions, TradeValidator} from '@colonist/game-mechanic-validators'
import {TranslationKeys} from '@colonist/i18n'
import styles from '../UIGameTradeOffers.module.scss'
import clsx from 'clsx'
import {T} from '../../../common/T'
import {CardEnum, TradeResponseStatus} from '@colonist/game-states'
import {ArrayUtils} from '@colonist/utils'
import {ModeSettingEnum} from '@colonist/game-base-types'
import {useGameStore} from '../../../store/GameStoreContext'

export interface UIGameTradeOfferCounterOfferButtonProps {
    className?: string | undefined
    tradeValidator: TradeValidator
    setIsOtherButtonsClicked: (isOtherButtonsClicked: boolean) => void
    disableTooltips?: boolean | undefined
}

export const UIGameTradeOfferCounterOfferButton = ({
    className,
    tradeValidator,
    setIsOtherButtonsClicked,
    disableTooltips,
}: UIGameTradeOfferCounterOfferButtonProps) => {
    const setCounterOffer = useGameStore((state) => state.gameTrade.setCounterOffer)
    const setWantedCards = useGameStore((state) => state.gameTrade.setWantedCards)
    const setIsShowTradePanel = useGameStore((state) => state.gameTrade.setIsShowTradePanel)
    const setSelectedCards = useGameStore((state) => state.gameCardInventory.setSelectedCards)
    const sendUpdateTradeResponse = useGameStore((state) => state.gameTradeOffers.sendUpdateTradeResponse)
    const modeSetting = useGameStore((state) => state.gameSettings.modeSetting)
    const isMyColorSpectator = useGameStore((state) => state.isMyColorSpectator)
    const myColor = useGameStore((state) => state.myColor)
    const counterOfferAction = tradeValidator.getProposeCounterOfferActionStatus(myColor)
    const isBeginnerMode = modeSetting == ModeSettingEnum.BeginnerModeClassic4P
    const isHidden = !counterOfferAction.displayActionButton || isBeginnerMode
    const tooltipText: TranslationKeys = 'strings:game.tooltips.createCounterOffer'
    const helperText = counterOfferAction.errorMessageIfCantTakeAction
    const isEnabled = counterOfferAction.canTakeAction && !isMyColorSpectator
    const myResponseStatus = tradeValidator.getPlayerResponseState(myColor)
    const isEmbargoed = myResponseStatus?.responseStatus == TradeResponseStatus.Embargoed

    const shouldCounterOfferButtonInitiateRegularTrade = modeSetting === ModeSettingEnum.ColonistRush4P

    const handleClick = () => {
        const givingResourcesWithoutAnyResourceCards = ArrayUtils
            .removeAllInstancesOfElementInArray(tradeValidator.getGivingResources(myColor), CardEnum.AnyResource)
        const receivingResourcesWithoutAnyResourceCards = ArrayUtils
            .removeAllInstancesOfElementInArray(tradeValidator.getReceivingResources(myColor), CardEnum.AnyResource)

        /*
         Read this discussion: https://github.com/colonistio/katan/pull/21758#discussion_r2580842683.
         Below if check is a solution to prevent counter offer creation in rush gamemode without adding a new state into the TradeState.
         Ideally, the backend should inform the client about when to prevent counter offer creation.
         Don't edit this logic without editing the isCounterOffersDisabled check in TradeCreationDataValidator.ts as well.
         */
        if(shouldCounterOfferButtonInitiateRegularTrade) {
            setSelectedCards(givingResourcesWithoutAnyResourceCards)
            setWantedCards(receivingResourcesWithoutAnyResourceCards)
            setIsShowTradePanel(true)
            return
        }
        setCounterOffer({
            tradeId: tradeValidator.state.id,
            receivingResources: receivingResourcesWithoutAnyResourceCards,
            givingResources: givingResourcesWithoutAnyResourceCards,
        })
        sendUpdateTradeResponse({id: tradeValidator.state.id, response: TradeActions.CounterOfferCreating})
    }
    if(isHidden) return null
    return (
        <UIGameActionButton
            tooltipContent={<T id={tooltipText}/>}
            tooltipDisabled={disableTooltips}
            className={clsx(styles.tradeButton, className)}
            iconPath={IconPencil}
            onClick={handleClick}
            buttonType="trade"
            tooltipPosition='bottom'
            centerTextTooltip
            helperText={helperText}
            disabled={!isEnabled}
            isEmbargoed={isEmbargoed}
        />
    )
}
